import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Grid,
  Divider
} from '@mui/material';
import {
  LocationOn,
  CheckCircle,
  CameraAlt,
  Send,
  Apartment,
  Place,
  Error as ErrorIcon,
  Close
} from '@mui/icons-material';

const CATEGORIES = {
  plomberie: { label: 'Plomberie', icon: '🚰' },
  electricite: { label: 'Électricité', icon: '⚡' },
  chauffage: { label: 'Chauffage', icon: '🔥' },
  proprete: { label: 'Propreté', icon: '🧹' },
  ascenseur: { label: 'Ascenseur', icon: '🛗' },
  securite: { label: 'Sécurité', icon: '🔒' },
  autre: { label: 'Autre', icon: '📋' }
};

const URGENCY_LEVELS = {
  urgent: { label: 'Urgent (24h)', color: 'error' },
  normal: { label: 'Normal (72h)', color: 'warning' },
  low: { label: 'Faible', color: 'info' }
};

export default function ScanQRCode() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [qrData, setQrData] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    urgency: 'normal',
    description: '',
    photos: [],
    reporterName: '',
    reporterEmail: '',
    reporterPhone: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [location, setLocation] = useState(null);

  useEffect(() => {
    fetchQRCodeData();
    getLocation();
  }, [code]);

  const fetchQRCodeData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/scan/${code}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        const qrInfo = data.qrcode || data;
        setQrData(qrInfo);
      } else {
        setQrData({ error: 'QR Code invalide' });
      }
    } catch (error) {
      console.error('Erreur fetch:', error);
      setQrData({ error: 'Erreur de connexion' });
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => console.log('Géolocalisation refusée')
      );
    }
  };

  // Compression simple avec Canvas
  const compressImage = (file, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculer les nouvelles dimensions
          if (width > maxWidth) {
            height = (maxWidth / width) * height;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convertir en base64 avec compression
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    try {
      const compressedPhotos = [];
      
      for (const file of files) {
        try {
          const compressed = await compressImage(file);
          compressedPhotos.push(compressed);
        } catch (err) {
          console.error('Erreur compression photo:', err);
          // En cas d'erreur, utiliser l'image originale
          const reader = new FileReader();
          const original = await new Promise((resolve) => {
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(file);
          });
          compressedPhotos.push(original);
        }
      }
      
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...compressedPhotos].slice(0, 5)
      }));
    } catch (error) {
      console.error('Erreur traitement photos:', error);
      setError('Erreur lors du traitement des photos');
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.description || !formData.category) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const token = localStorage.getItem('token');
    
    // Si pas de token, demander les infos de contact
    if (!token && (!formData.reporterEmail || !formData.reporterName)) {
      setError('Veuillez renseigner votre nom et email');
      return;
    }

    setSubmitting(true);
    setError('');
    
    try {
      const payload = {
        qrCodeId: qrData?.id,
        category: formData.category,
        urgency: formData.urgency,
        description: formData.description,
        photos: formData.photos,
        location,
        reporterName: formData.reporterName,
        reporterEmail: formData.reporterEmail,
        reporterPhone: formData.reporterPhone
      };

      const response = await fetch('/api/reports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate(token ? '/interventions' : '/');
        }, 3000);
      } else {
        setError(responseData.error || 'Erreur lors de l\'envoi du signalement');
      }
    } catch (error) {
      console.error('Erreur envoi:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setSubmitting(false);
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="success" icon={<CheckCircle />}>
          <Typography variant="h6">Signalement envoyé avec succès !</Typography>
          <Typography>
            Nous avons bien reçu votre signalement. Un technicien interviendra dans les plus brefs délais.
          </Typography>
          <Typography variant="caption" display="block" mt={2}>
            Redirection en cours...
          </Typography>
        </Alert>
      </Container>
    );
  }

  if (qrData?.error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">{qrData.error}</Alert>
      </Container>
    );
  }

  const isAnonymous = !localStorage.getItem('token');

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Header */}
          <Box textAlign="center">
            <Typography variant="h4" fontWeight="bold" color="primary">
              SQUARE COM
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Système de signalement
            </Typography>
          </Box>

          <Divider />

          {/* Informations de localisation */}
          {qrData && (
            <Card sx={{ bgcolor: 'grey.50' }}>
              <CardContent>
                <Stack spacing={2}>
                  {qrData.residenceName && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Apartment color="primary" />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Résidence
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {qrData.residenceName}
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                  
                  {qrData.location && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Place color="primary" />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Emplacement
                        </Typography>
                        <Typography variant="body1">
                          {qrData.location}
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Formulaire */}
          <Typography variant="h6">
            Signaler un problème
          </Typography>

          {/* Si utilisateur non connecté, demander les infos */}
          {isAnonymous && (
            <>
              <TextField
                fullWidth
                required
                label="Votre nom"
                value={formData.reporterName}
                onChange={(e) => setFormData({...formData, reporterName: e.target.value})}
              />
              <TextField
                fullWidth
                required
                label="Votre email"
                type="email"
                value={formData.reporterEmail}
                onChange={(e) => setFormData({...formData, reporterEmail: e.target.value})}
              />
              <TextField
                fullWidth
                label="Votre téléphone (optionnel)"
                value={formData.reporterPhone}
                onChange={(e) => setFormData({...formData, reporterPhone: e.target.value})}
              />
              <Divider />
            </>
          )}

          <FormControl fullWidth required>
            <InputLabel>Type de problème</InputLabel>
            <Select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              label="Type de problème"
            >
              {Object.entries(CATEGORIES).map(([key, cat]) => (
                <MenuItem key={key} value={key}>
                  <Stack direction="row" spacing={1}>
                    <span>{cat.icon}</span>
                    <span>{cat.label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Urgence</InputLabel>
            <Select
              value={formData.urgency}
              onChange={(e) => setFormData({...formData, urgency: e.target.value})}
              label="Urgence"
            >
              {Object.entries(URGENCY_LEVELS).map(([key, level]) => (
                <MenuItem key={key} value={key}>
                  <Chip 
                    label={level.label} 
                    color={level.color} 
                    size="small"
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            required
            multiline
            rows={4}
            label="Description du problème"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Décrivez le problème en détail..."
          />

          <Button
            variant="outlined"
            component="label"
            startIcon={<CameraAlt />}
            fullWidth
          >
            Ajouter des photos ({formData.photos.length}/5)
            <input
              type="file"
              hidden
              accept="image/*"
              multiple
              capture="environment"
              onChange={handlePhotoUpload}
            />
          </Button>

          {formData.photos.length > 0 && (
            <Grid container spacing={1}>
              {formData.photos.map((photo, idx) => (
                <Grid item xs={4} key={idx}>
                  <Box position="relative">
                    <img
                      src={photo}
                      alt={`Photo ${idx + 1}`}
                      style={{
                        width: '100%',
                        height: '80px',
                        objectFit: 'cover',
                        borderRadius: '8px'
                      }}
                    />
                    <Button
                      size="small"
                      onClick={() => removePhoto(idx)}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        minWidth: 24,
                        width: 24,
                        height: 24,
                        p: 0,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' }
                      }}
                    >
                      ×
                    </Button>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {location && (
            <Chip 
              icon={<LocationOn />} 
              label="Position GPS enregistrée" 
              color="success"
              variant="outlined"
            />
          )}

          {error && (
            <Alert severity="error" icon={<ErrorIcon />}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
            onClick={handleSubmit}
            disabled={submitting}
            fullWidth
          >
            {submitting ? 'Envoi en cours...' : 'Envoyer le signalement'}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}
